import type { TFunction } from 'i18next';
import {
  CV_FIELD_MAX,
  formatYearMonthForPicker,
  parseCvPickerDate,
  cvCertificationSchema,
  cvEducationSchema,
  cvHeadlineSchema,
  cvHighlightSchema,
  cvLanguageEntrySchema,
  cvSkillCategorySchema,
  cvSkillEntrySchema,
  cvSummarySchema,
} from '@tabbio/utils';
import type { CVCertification, CVEducation, CVHighlight } from '../../../lib/cv-mock-data';

// Schemas in @tabbio/utils emit i18n key strings as messages. These wrappers
// run safeParse and turn the first issue per field path into a translated
// string, interpolating { max } for `cv.edit.tooLong` based on CV_FIELD_MAX.

function translateIssueMessage(message: string, maxKey: string | undefined, t: TFunction): string {
  if (message === 'cv.edit.tooLong') {
    const max = maxKey ? CV_FIELD_MAX[maxKey] : undefined;
    return t(message, { max });
  }
  return t(message);
}

export interface SummaryDraft {
  headline: string;
  summary: string;
}
export interface SummaryErrors {
  headline?: string;
  summary?: string;
}

export function validateSummary(draft: SummaryDraft, t: TFunction): SummaryErrors {
  const errors: SummaryErrors = {};
  const headlineResult = cvHeadlineSchema.safeParse(draft.headline);
  if (!headlineResult.success) {
    const issue = headlineResult.error.issues[0];
    errors.headline = translateIssueMessage(issue.message, 'headline', t);
  }
  const summaryResult = cvSummarySchema.safeParse(draft.summary);
  if (!summaryResult.success) {
    const issue = summaryResult.error.issues[0];
    errors.summary = translateIssueMessage(issue.message, 'summary', t);
  }
  return errors;
}

export interface EducationErrors {
  degree?: string;
  school?: string;
  year?: string;
  description?: string;
}

/**
 * Repair an imported/legacy education year into the picker's own format
 * ("Mar-2015" → "Mar 2015"). Text carrying no readable date becomes "", which
 * is allowed: many CVs list a school with no graduation year.
 */
export function normalizeEducationYear(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (parseCvPickerDate(trimmed)) return trimmed;
  return formatYearMonthForPicker(trimmed);
}

export function validateEducation(draft: CVEducation, t: TFunction): EducationErrors {
  const result = cvEducationSchema.safeParse(draft);
  if (result.success) return {};
  const errors: EducationErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof EducationErrors | undefined;
    if (!field || errors[field]) continue;
    errors[field] = translateIssueMessage(issue.message, field, t);
  }
  return errors;
}

export interface CertificationErrors {
  name?: string;
  issuer?: string;
  date?: string;
  credentialUrl?: string;
}

export interface HighlightErrors {
  title?: string;
  desc?: string;
}

export function validateHighlight(draft: CVHighlight, t: TFunction): HighlightErrors {
  const result = cvHighlightSchema.safeParse(draft);
  if (result.success) return {};
  const errors: HighlightErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof HighlightErrors | undefined;
    if (!field || errors[field]) continue;
    errors[field] = translateIssueMessage(issue.message, field, t);
  }
  return errors;
}

export function validateCertification(draft: CVCertification, t: TFunction): CertificationErrors {
  const result = cvCertificationSchema.safeParse(draft);
  if (result.success) return {};
  const errors: CertificationErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof CertificationErrors | undefined;
    if (!field || errors[field]) continue;
    errors[field] = translateIssueMessage(issue.message, field, t);
  }
  return errors;
}

export function validateLanguageEntry(value: string, existing: string[], t: TFunction): string | undefined {
  const result = cvLanguageEntrySchema.safeParse(value);
  if (!result.success) {
    return translateIssueMessage(result.error.issues[0].message, 'language', t);
  }
  const normalized = result.data.toLowerCase();
  if (existing.some((item) => item.trim().toLowerCase() === normalized)) {
    return t('cv.edit.duplicateLanguage');
  }
  return undefined;
}

export function validateSkillEntry(value: string, existing: string[], t: TFunction): string | undefined {
  const result = cvSkillEntrySchema.safeParse(value);
  if (!result.success) {
    return translateIssueMessage(result.error.issues[0].message, 'skill', t);
  }
  const normalized = result.data.toLowerCase();
  if (existing.some((item) => item.trim().toLowerCase() === normalized)) {
    return t('cv.edit.duplicateSkill');
  }
  return undefined;
}

export function validateSkillCategory(value: string, t: TFunction): string | undefined {
  const result = cvSkillCategorySchema.safeParse(value);
  if (!result.success) {
    return translateIssueMessage(result.error.issues[0].message, 'category', t);
  }
  return undefined;
}
